import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import { Coins, Plus, Play, X, Trash2, Pencil } from "lucide-react";

const TRIGGERS = ["sanction", "disbursement", "closed_won"];
const METRICS = ["disbursement_inr", "sanction_amount_inr", "revenue_inr"];
const PRODUCT_HINTS = ["Term Loan", "Working Capital", "Invoice Discounting", "LAP", "Business Loan", "Revenue-Based Financing"];
const ROLE_HINTS = ["debt_agent", "debt_manager", "debt_head", "channel_partner_manager"];

const emptyRule = () => ({
  name: "",
  description: "",
  trigger_event: "disbursement",
  metric: "disbursement_inr",
  role_scope: [],
  user_scope: [],
  product_scope: [],
  stage_scope: [],
  slabs: [{ min_inr: 0, max_inr: 10000000, rate_pct: 0.5, flat_bonus_inr: 0 }],
  threshold_pct_team_achievement: 100,
  team_multiplier: 1.25,
  threshold_pct_individual_achievement: 60,
  active: true,
});

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function IncentiveRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyRule());
  const [editingId, setEditingId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [simInput, setSimInput] = useState({ trigger_event: "disbursement", amount_inr: 15000000, actor_role: "debt_agent", product: "Term Loan", individual_achievement_pct: 100, team_achievement_pct: 100 });
  const [simResult, setSimResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/incentive-rules");
      setRules(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const body = { ...draft };
      body.slabs = draft.slabs.map((s) => ({
        min_inr: Number(s.min_inr) || 0,
        max_inr: s.max_inr === "" || s.max_inr == null ? null : Number(s.max_inr),
        rate_pct: Number(s.rate_pct) || 0,
        flat_bonus_inr: Number(s.flat_bonus_inr) || 0,
      }));
      if (editingId) {
        await api.patch(`/admin/incentive-rules/${editingId}`, body);
        toast.success("Rule updated");
      } else {
        await api.post("/admin/incentive-rules", body);
        toast.success("Rule created");
      }
      setCreating(false);
      setEditingId(null);
      setDraft(emptyRule());
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditor = (rule) => {
    setEditingId(rule.rule_id);
    setDraft({
      ...emptyRule(),
      ...rule,
      slabs: (rule.slabs || []).map((s) => ({ ...s })),
    });
    setCreating(true);
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this rule?")) return;
    try {
      await api.delete(`/admin/incentive-rules/${id}`);
      toast.success("Rule deactivated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const simulate = async () => {
    try {
      const { data } = await api.post("/admin/incentive-rules/simulate", {
        ...simInput,
        amount_inr: Number(simInput.amount_inr),
        individual_achievement_pct: Number(simInput.individual_achievement_pct),
        team_achievement_pct: Number(simInput.team_achievement_pct),
      });
      setSimResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeCount = useMemo(() => rules.filter((r) => r.active).length, [rules]);
  const visibleRules = useMemo(() => showArchived ? rules : rules.filter((r) => r.active), [rules, showArchived]);

  return (
    <DashboardShell title="Incentive Rules" subtitle="Admin · Advanced multi-condition payout rules">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-incentive-rules">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Coins size={13} className="text-navy" />
            <span><b>{activeCount}</b> active · <b>{rules.length - activeCount}</b> archived</span>
            <label className="ml-3 inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="rule-show-archived"
              />
              <span>Show archived</span>
            </label>
          </div>
          <button
            onClick={() => { setCreating(true); setEditingId(null); setDraft(emptyRule()); }}
            className="text-[12px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 rounded px-3 py-1.5 inline-flex items-center gap-1"
            data-testid="rule-create-open"
          >
            <Plus size={12} /> New rule
          </button>
        </div>

        {/* Rules table */}
        <div className="border border-ink/10 rounded-lg bg-white overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-navy/5 text-navy">
              <tr>
                <th className="text-left px-3 py-2 border-b border-ink/10">Name</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Trigger</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Scope</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Slabs</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Team ×</th>
                <th className="text-left px-3 py-2 border-b border-ink/10">Status</th>
                <th className="px-3 py-2 border-b border-ink/10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-4 text-xs italic text-ink-muted">Loading…</td></tr>}
              {!loading && visibleRules.length === 0 && <tr><td colSpan={7} className="p-4 text-xs italic text-ink-muted">No rules to show. {rules.length > 0 && !showArchived && "Toggle 'Show archived' to see deactivated rules."}{rules.length === 0 && "Create one to start paying out incentives."}</td></tr>}
              {visibleRules.map((r) => (
                <tr key={r.rule_id} className="border-b border-ink/5 align-top" data-testid={`rule-row-${r.rule_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-heading text-navy">{r.name}</div>
                    {r.description && <div className="text-[10px] text-ink-muted italic">{r.description}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {r.trigger_event} → {r.metric}
                    {r.threshold_pct_individual_achievement > 0 && (
                      <div className="text-[10px] text-ink-muted">≥ {r.threshold_pct_individual_achievement}% self target</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {(r.role_scope || []).length > 0 && <div>roles: <span className="font-mono">{r.role_scope.join(", ")}</span></div>}
                    {(r.product_scope || []).length > 0 && <div>products: <span className="font-mono">{r.product_scope.join(", ")}</span></div>}
                    {(r.stage_scope || []).length > 0 && <div>stages: <span className="font-mono">{r.stage_scope.join(", ")}</span></div>}
                    {(r.user_scope || []).length > 0 && <div>users: <span className="font-mono">{r.user_scope.length} scoped</span></div>}
                    {["role_scope","product_scope","stage_scope","user_scope"].every((k) => !(r[k] || []).length) && (
                      <span className="text-ink-muted italic">all</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {(r.slabs || []).map((s, i) => (
                      <div key={i} className="font-mono">
                        {inr(s.min_inr)}–{s.max_inr == null ? "∞" : inr(s.max_inr)} · {s.rate_pct}%
                        {s.flat_bonus_inr > 0 && ` + ${inr(s.flat_bonus_inr)}`}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {r.team_multiplier}× @ ≥{r.threshold_pct_team_achievement}%
                  </td>
                  <td className="px-3 py-2">
                    {r.active ? (
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">ACTIVE</span>
                    ) : (
                      <span className="text-[10px] bg-ink/10 text-ink-muted px-1.5 py-0.5 rounded-full">ARCHIVED</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.active && (
                      <>
                        <button onClick={() => openEditor(r)} title="Edit" data-testid={`rule-edit-${r.rule_id}`} className="text-navy hover:bg-navy/10 rounded p-1 mr-1">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => deactivate(r.rule_id)} title="Deactivate" data-testid={`rule-deact-${r.rule_id}`} className="text-red-600 hover:bg-red-50 rounded p-1">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Simulator */}
        <div className="border border-ink/10 rounded-lg bg-navy/[0.02] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Play size={13} className="text-navy" />
            <h3 className="text-sm font-heading text-navy">Payout simulator</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SmallField label="Trigger" value={simInput.trigger_event} onChange={(v) => setSimInput({ ...simInput, trigger_event: v })} options={TRIGGERS} testId="sim-trigger" />
            <SmallField label="Amount (₹)" value={simInput.amount_inr} onChange={(v) => setSimInput({ ...simInput, amount_inr: v })} testId="sim-amount" />
            <SmallField label="Actor role" value={simInput.actor_role} onChange={(v) => setSimInput({ ...simInput, actor_role: v })} testId="sim-role" />
            <SmallField label="Product" value={simInput.product} onChange={(v) => setSimInput({ ...simInput, product: v })} testId="sim-product" />
            <SmallField label="Individual %" value={simInput.individual_achievement_pct} onChange={(v) => setSimInput({ ...simInput, individual_achievement_pct: v })} testId="sim-indiv" />
            <SmallField label="Team %" value={simInput.team_achievement_pct} onChange={(v) => setSimInput({ ...simInput, team_achievement_pct: v })} testId="sim-team" />
            <div className="col-span-2 md:col-span-2 flex items-end justify-end">
              <button onClick={simulate} className="text-[12px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 rounded px-3 py-1.5" data-testid="sim-run">
                Simulate
              </button>
            </div>
          </div>
          {simResult && (
            <div className="mt-3 border-t border-ink/10 pt-3">
              <div className="text-xs">
                <b className="text-navy">Grand total: {inr(simResult.grand_total_inr)}</b> · {simResult.matched_rules?.length || 0} rule{simResult.matched_rules?.length === 1 ? "" : "s"} matched
              </div>
              <div className="mt-2 space-y-1 text-[11px] font-mono">
                {(simResult.matched_rules || []).map((m) => (
                  <div key={m.rule_id} className="border border-ink/10 rounded p-2 bg-white">
                    <div><b>{m.rule_name}</b> → {inr(m.gross_payout_inr)} (base {inr(m.base_payout_inr)} × {m.team_multiplier_applied})</div>
                    {(m.slab_breakdown || []).map((sb, i) => (
                      <div key={i} className="text-ink-muted pl-3">
                        {inr(sb.min_inr)}–{sb.max_inr == null ? "∞" : inr(sb.max_inr)} band {inr(sb.band_amount_inr)} · {sb.rate_pct}% = {inr(sb.slab_payout_inr)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Create / edit modal */}
        {creating && (
          <RuleEditor
            draft={draft}
            setDraft={setDraft}
            editing={!!editingId}
            onCancel={() => { setCreating(false); setEditingId(null); setDraft(emptyRule()); }}
            onCreate={create}
          />
        )}
      </div>
    </DashboardShell>
  );
}

function SmallField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] font-heading text-ink-muted uppercase tracking-wider mb-1">{label}</div>
      {options ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full text-[12px] font-mono border border-ink/15 rounded px-2 py-1.5 bg-white">
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full text-[12px] font-mono border border-ink/15 rounded px-2 py-1.5 bg-white" />
      )}
    </label>
  );
}

function RuleEditor({ draft, setDraft, editing, onCancel, onCreate }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setSlab = (i, k, v) => {
    setDraft((d) => ({
      ...d,
      slabs: d.slabs.map((s, j) => (j === i ? { ...s, [k]: v } : s)),
    }));
  };
  const addSlab = () => setDraft((d) => {
    const last = d.slabs[d.slabs.length - 1];
    const nextMin = last?.max_inr ?? 0;
    return { ...d, slabs: [...d.slabs, { min_inr: nextMin, max_inr: null, rate_pct: 0.5, flat_bonus_inr: 0 }] };
  });
  const removeSlab = (i) => setDraft((d) => ({ ...d, slabs: d.slabs.filter((_, j) => j !== i) }));
  const csv = (arr) => (arr || []).join(", ");
  const parseCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-auto py-8">
      <div className="bg-white w-full max-w-3xl mx-4 rounded-lg border border-ink/10 shadow-xl p-5" data-testid="rule-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-heading text-navy">{editing ? "Edit incentive rule" : "Create incentive rule"}</h3>
          <button onClick={onCancel} className="text-ink-muted hover:text-navy" data-testid="rule-editor-close"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SmallField label="Name" value={draft.name} onChange={(v) => set("name", v)} testId="rule-name" />
          <SmallField label="Trigger" value={draft.trigger_event} onChange={(v) => set("trigger_event", v)} options={TRIGGERS} testId="rule-trigger" />
          <SmallField label="Metric" value={draft.metric} onChange={(v) => set("metric", v)} options={METRICS} testId="rule-metric" />
          <SmallField label="Description" value={draft.description} onChange={(v) => set("description", v)} testId="rule-desc" />
          <SmallField label={`Role scope (CSV) — hints: ${ROLE_HINTS.join(",")}`} value={csv(draft.role_scope)} onChange={(v) => set("role_scope", parseCsv(v))} testId="rule-roles" />
          <SmallField label={`Product scope (CSV) — hints: ${PRODUCT_HINTS.join(",")}`} value={csv(draft.product_scope)} onChange={(v) => set("product_scope", parseCsv(v))} testId="rule-products" />
          <SmallField label="Stage scope (CSV)" value={csv(draft.stage_scope)} onChange={(v) => set("stage_scope", parseCsv(v))} testId="rule-stages" />
          <SmallField label="User scope (user_ids CSV)" value={csv(draft.user_scope)} onChange={(v) => set("user_scope", parseCsv(v))} testId="rule-users" />
          <SmallField label="Individual achievement gate (%)" value={draft.threshold_pct_individual_achievement} onChange={(v) => set("threshold_pct_individual_achievement", Number(v) || 0)} testId="rule-indiv" />
          <SmallField label="Team achievement threshold (%)" value={draft.threshold_pct_team_achievement} onChange={(v) => set("threshold_pct_team_achievement", Number(v) || 0)} testId="rule-team-th" />
          <SmallField label="Team multiplier (×)" value={draft.team_multiplier} onChange={(v) => set("team_multiplier", Number(v) || 1)} testId="rule-team-mult" />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-heading text-navy uppercase tracking-wider">Slabs</h4>
            <button onClick={addSlab} className="text-[11px] text-navy border border-navy/30 rounded px-2 py-1" data-testid="rule-add-slab">+ Slab</button>
          </div>
          <div className="space-y-2">
            {draft.slabs.map((s, i) => (
              <div key={i} className="grid grid-cols-5 gap-2 items-end">
                <SmallField label={`Slab ${i + 1} · Min (₹)`} value={s.min_inr} onChange={(v) => setSlab(i, "min_inr", v)} testId={`slab-min-${i}`} />
                <SmallField label="Max (₹, blank = ∞)" value={s.max_inr ?? ""} onChange={(v) => setSlab(i, "max_inr", v === "" ? null : v)} testId={`slab-max-${i}`} />
                <SmallField label="Rate %" value={s.rate_pct} onChange={(v) => setSlab(i, "rate_pct", v)} testId={`slab-rate-${i}`} />
                <SmallField label="Flat bonus (₹)" value={s.flat_bonus_inr} onChange={(v) => setSlab(i, "flat_bonus_inr", v)} testId={`slab-flat-${i}`} />
                <button onClick={() => removeSlab(i)} className="text-red-600 hover:bg-red-50 rounded px-2 py-1 text-[11px]" data-testid={`slab-del-${i}`}>Remove</button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-ink/10">
          <button onClick={onCancel} className="text-[12px] text-ink-muted hover:text-navy px-3 py-1.5" data-testid="rule-cancel">Cancel</button>
          <button onClick={onCreate} className="text-[12px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 rounded px-3 py-1.5" data-testid="rule-submit">{editing ? "Save changes" : "Create rule"}</button>
        </div>
      </div>
    </div>
  );
}
